import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { heroContent, expertise, mariamPhotos, culturalArt } from '../data/mock';

const Home = () => {
  const heroRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);

  // About images
  const aboutImages = {
    portrait: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/jmiwoqg0_1398103d-0ba7-4620-a3b3-c613e3875745%202.JPG",
    withHusband: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/nbcwo0os_CEA96EC5-1247-4512-A4C5-D9D73C88C553.jpeg",
    groupPhoto: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/yt680ebf_PHOTO-2026-02-09-14-34-26.jpg",
    womenGathering: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/5w1xa077_IMG_6856.JPG",
    bwPortrait: "https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/h46d6hak_PHOTO-2026-02-09-14-37-42.jpg"
  };

  const interests = [
    "On a golf course (thinking more than swinging)",
    "Playing or watching tennis and basketball",
    "Reading about psychology, finance, identity, and history",
    "Deep in conversations about markets, money, and meaning",
    "Supporting philanthropic work and causes that create real leverage—not just visibility"
  ];

  const values = [
    "Building things that last",
    "Creating clarity in complex environments",
    "Helping people step into rooms with confidence and cultural fluency",
    "Using privilege, access, and knowledge responsibly",
    "Leaving behind work that matters more than optics"
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      if (heroRef.current) {
        heroRef.current.style.transform = `translateY(${window.scrollY * 0.3}px)`;
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="bg-black min-h-screen">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center overflow-hidden">
        {/* Background Video with Parallax */}
        <div 
          ref={heroRef}
          className="absolute inset-0 z-0"
        >
          {/* Video Background */}
          <video
            autoPlay
            muted
            loop
            playsInline
            poster={mariamPhotos.hero}
            className="w-full h-[120%] object-cover object-center"
          >
            <source 
              src="https://customer-assets.emergentagent.com/job_mariam-editorial/artifacts/xxy13i0z_VIDEO-2026-02-09-14-37-05.mp4" 
              type="video/mp4" 
            />
            {/* Fallback image for browsers that don't support video */}
            <img
              src={mariamPhotos.hero}
              alt="Mariam Sallam"
              className="w-full h-full object-cover"
            />
          </video>
          
          {/* Dark overlay - 55% black */}
          <div className="absolute inset-0 bg-black/55" />
          
          {/* Deep red gradient from bottom-left fading upward */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top right, rgba(107, 15, 26, 0.5) 0%, rgba(74, 14, 14, 0.3) 30%, transparent 60%)'
            }}
          />
          
          {/* Additional top fade for navbar readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12 w-full">
          <div className="max-w-2xl">
            {/* Subtle backdrop blur container for text readability */}
            <div className="relative">
              {/* Blur background - subtle */}
              <div className="absolute -inset-6 lg:-inset-10 bg-black/20 backdrop-blur-[2px] rounded-sm -z-10" />
              
              {/* Overline */}
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/60 mb-6">
                Cultural Strategist · Technical Enthusiast
              </p>

              {/* Main Headline - Editorial Style */}
              <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl xl:text-8xl text-white font-normal leading-[0.95] tracking-tight">
                {heroContent.headline}
              </h1>
              <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-white/90 italic mt-2 tracking-tight">
                {heroContent.subheadline}
              </p>

              {/* Description */}
              <p className="font-sans text-base text-white/70 max-w-md mt-10 leading-relaxed font-light">
                {heroContent.description}
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 mt-12">
                <Link
                  to="/philanthropy"
                  className="inline-flex items-center justify-center px-10 py-4 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all duration-300"
                >
                  Learn More
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center px-10 py-4 border border-white/40 text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-all duration-300"
                >
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10">
          <div className="flex flex-col items-center gap-3">
            <span className="font-sans text-[10px] tracking-[0.3em] uppercase text-white/40">
              Scroll
            </span>
            <div className="w-[1px] h-12 bg-gradient-to-b from-white/40 to-transparent" />
          </div>
        </div>
      </section>

      {/* About Section - Portrait & Intro */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Image Column */}
            <div className="lg:col-span-5">
              <img
                src={aboutImages.portrait}
                alt="Mariam Sallam"
                className="w-full aspect-[3/4] object-cover object-top"
              />
            </div>

            {/* Content Column */}
            <div className="lg:col-span-7">
              {/* Overline */}
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-6">
                About
              </p>

              {/* Headline */}
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-normal leading-[1.1] tracking-tight mb-12">
                Cultural strategist, technical enthusiast, global connector, and lifelong student of people, power, and purpose.
              </h2>

              {/* Intro */}
              <div className="space-y-6">
                <p className="font-sans text-base text-white/70 leading-[1.8] font-light">
                  I'm Mariam—cultural strategist, technical enthusiast, global connector, and lifelong student of people, power, and purpose.
                </p>
                <p className="font-sans text-base text-white/70 leading-[1.8] font-light">
                  I'm married to a venture capitalist, which means my dinner conversations range from geopolitics to startups to why certain ideas scale while others quietly disappear. I live at the intersection of culture, capital, and influence, and I'm endlessly fascinated by how narratives shape outcomes—whether for individuals, institutions, or entire regions.
                </p>
                <p className="font-serif text-xl text-white italic mt-8">
                  At my core, I'm someone who believes culture is not decoration—it's infrastructure.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Life & Partnership */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <div className="lg:order-2">
              <img
                src={aboutImages.withHusband}
                alt="Mariam and her husband"
                className="w-full aspect-square object-cover"
              />
            </div>
            <div className="lg:order-1">
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Life & Partnership
              </p>
              <p className="font-sans text-base text-white/70 leading-[1.8] font-light">
                I love travel—not just destinations, but context. I care about why cities feel the way they do, how power moves through spaces, and what history leaves behind in architecture, language, and behavior.
              </p>
              <p className="font-sans text-base text-white/70 leading-[1.8] font-light mt-6">
                I'm drawn to books that explain how people think, how systems work, and how influence is built—from behavioral psychology to finance to cultural history. I read to sharpen perspective, not just to relax.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10 relative">
        {/* Subtle red gradient at top */}
        <div className="absolute top-0 left-0 right-0 h-[400px] bg-gradient-to-b from-[#6B0F1A]/10 to-transparent pointer-events-none" />
        
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 relative">
          {/* Section Header */}
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16 lg:mb-20">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                Areas of Focus
              </p>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-normal tracking-tight">
                Expertise
              </h2>
            </div>
            <p className="font-sans text-sm text-white/50 max-w-sm leading-relaxed font-light">
              Two decades of strategic experience across culture, brand, and global influence.
            </p>
          </div>

          {/* Expertise Grid - Editorial Blocks */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            {expertise.map((item, index) => (
              <div
                key={index}
                className="group py-10 lg:py-14 border-t border-white/10 first:border-t-0 md:first:border-t md:odd:pr-12 md:even:pl-12 md:odd:border-r md:even:border-r-0 border-white/10"
              >
                <span className="font-serif text-5xl lg:text-6xl text-white/10 block mb-6 transition-colors duration-500 group-hover:text-white/20">
                  0{index + 1}
                </span>
                <h3 className="font-serif text-2xl lg:text-3xl text-white mb-4 tracking-tight">
                  {item.title}
                </h3>
                <p className="font-sans text-sm text-white/50 leading-relaxed font-light">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-14 md:py-24 lg:py-24 bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 lg:gap-24">
            {/* Mission */}
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
                My Mission
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-black font-normal leading-[1.15] tracking-tight">
                To translate culture into strategy, and strategy into impact.
              </h2>
              <p className="font-sans text-base text-black/60 mt-6 leading-[1.8] font-light">
                I help people and institutions understand who they are, what they stand for, and how to move through the world with clarity, confidence, and integrity—without losing their roots.
              </p>
            </div>
            {/* Vision */}
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-black/40 mb-4">
                My Vision
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-black font-normal leading-[1.15] tracking-tight">
                A world where heritage and modernity don't compete, but collaborate.
              </h2>
              <p className="font-sans text-base text-black/60 mt-6 leading-[1.8] font-light">
                Where Arab culture is not explained, defended, or diluted—but understood, respected, and positioned with strength on the global stage.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How I Live & Think */}
      <section className="py-14 md:py-24 lg:py-24">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div>
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                How I Live & Think
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-normal leading-[1.15] tracking-tight mb-10">
                When I'm not working, you'll find me:
              </h2>
              <div className="space-y-0">
                {interests.map((item, index) => (
                  <div key={index} className="py-4 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/70 font-light">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <img
                src={aboutImages.groupPhoto}
                alt="Mariam at an event"
                className="w-full h-auto max-h-[500px] object-contain object-top"
              />
            </div>
          </div>
        </div>
      </section>

      {/* What Drives Me */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <div className="lg:order-2">
              <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-4">
                What Drives Me
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-white font-normal leading-[1.15] tracking-tight mb-10">
                I care about:
              </h2>
              <div className="space-y-0">
                {values.map((item, index) => (
                  <div key={index} className="py-4 border-t border-white/10 first:border-t-0">
                    <p className="font-sans text-base text-white/70 font-light">{item}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="lg:order-1">
              <img
                src={aboutImages.womenGathering}
                alt="Community gathering"
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Belief / Quote Divider */}
      <section className="py-14 md:py-24 lg:py-32 relative overflow-hidden">
        {/* Cultural art as subtle background texture */}
        <div className="absolute inset-0 opacity-[0.03]">
          <img
            src={culturalArt.carpet}
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-white/40 mb-8">
              Core Belief
            </p>
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-white font-normal leading-[1.2] tracking-tight">
              Everything I do—across culture, strategy, advisory, and storytelling—is connected by one belief:
            </p>
            <p className="font-serif text-3xl md:text-4xl lg:text-5xl text-white italic mt-6 tracking-tight">
              When you understand culture, you understand power.
            </p>
          </div>
        </div>
      </section>

      {/* Contact CTA Section */}
      <section className="py-14 md:py-24 lg:py-24 border-t border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-10">
            <div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white font-normal tracking-tight">
                Ready to Begin?
              </h2>
              <p className="font-sans text-base text-white/50 max-w-lg mt-6 leading-relaxed font-light">
                Whether it's a strategic consultation, speaking engagement, or collaborative project—let's start a conversation.
              </p>
            </div>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center px-12 py-5 bg-gradient-to-r from-[#7B1818] to-[#4A0E0E] text-white font-sans text-[11px] tracking-[0.2em] uppercase hover:from-[#8B2020] hover:to-[#5A1010] transition-all duration-300"
            >
              Get in Touch
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
